import React from 'react';
import { BarChart3, TrendingUp } from 'lucide-react';
import MoviePoster from '../viewer/MoviePoster';

const OverallResults = ({ categories, movies, loading, overallResults, onMovieClick }) => {

  const getMovieById = (movieId) => {
    // Handle both string and number IDs by converting both to strings for comparison
    const searchId = String(movieId);
    return movies.find(movie => String(movie.id) === searchId);
  };

  // Results are now passed as a prop from the parent component

  // Aggregate results across all categories with vote deduplication
  const getAggregatedResults = () => {
    console.log('🔍 getAggregatedResults called with overallResults:', overallResults);
    
    if (!overallResults || typeof overallResults !== 'object') {
      console.log('❌ overallResults is null or not an object:', overallResults);
      return [];
    }
    
    console.log('🔍 overallResults keys:', Object.keys(overallResults));
    console.log('🔍 categories passed to OverallResults:', categories.length);
    console.log('🔍 categories sample:', categories.slice(0, 2).map(c => ({ 
      id: c.id, 
      name: c.name, 
      hasMovieIds: !!c.movie_ids,
      movieIdsLength: c.movie_ids?.length || 0 
    })));
    
    if (Object.keys(overallResults).length === 0) {
      console.log('ℹ️ overallResults is empty object');
      return [];
    }
    
    const movieAggregates = {};
    
    // Process each movie's results (overallResults has movieId as keys, not categoryId)
    Object.entries(overallResults).forEach(([movieId, movieResults]) => {
      console.log(`🎬 Processing movie ID: ${movieId}`);
      const movie = getMovieById(movieId);
      console.log(`🎬 Movie found:`, !!movie, movie?.title || 'N/A');
      if (!movie) {
        console.warn(`⚠️ Movie not found for ID: ${movieId}`);
        return;
      }
      
      // The Web Worker has already aggregated the results, so we can use them directly
      movieAggregates[movieId] = {
        movieId,
        movie,
        totalVotes: movieResults.totalVotes || 0,
        totalScore: movieResults.totalScore || 0,
        averageScore: movieResults.averageScore || 0,
        distribution: movieResults.distribution || { '-1': 0, '0': 0, '1': 0, '2': 0, '3': 0 },
        categoryCount: movieResults.categoryCount || 0
      };
      
      console.log(`✅ Added movie: ${movie.title} (${movieResults.totalVotes} votes, avg: ${movieResults.averageScore})`);
    });
    
    // Web Worker has already calculated everything, no need to recalculate
    
    // Sort by average score (highest first), then by vote count (highest first) for tiebreaking
    const sortedResults = Object.values(movieAggregates)
      .sort((a, b) => {
        // Primary sort: average score (highest first)
        if (b.averageScore !== a.averageScore) {
          return b.averageScore - a.averageScore;
        }
        // Tiebreaker: total votes (highest first)
        return b.totalVotes - a.totalVotes;
      });
    
    console.log('✅ getAggregatedResults completed:', {
      totalMovies: sortedResults.length,
      sampleResults: sortedResults.slice(0, 3).map(r => ({ 
        title: r.movie.title, 
        avgScore: r.averageScore, 
        totalVotes: r.totalVotes 
      }))
    });
    
    return sortedResults;
  };

  const getScoreColor = (score) => {
    if (score >= 2.5) return 'text-red-400'; // I would sell my soul (3 points)
    if (score >= 1.5) return 'text-orange-400'; // I want to watch (2 points)
    if (score >= 0.5) return 'text-yellow-400'; // I'd watch it (1 point)
    if (score >= -0.5) return 'text-gray-400'; // Ehhhh (0 points)
    return 'text-red-600'; // No thanks (-1 points)
  };

  const getScoreEmoji = (score) => {
    if (score >= 2.5) return '👹'; // I would sell my soul (3 points)
    if (score >= 1.5) return '🔥'; // I want to watch (2 points)
    if (score >= 0.5) return '👍'; // I'd watch it (1 point)
    if (score >= -0.5) return '😐'; // Ehhhh (0 points)
    return '👎'; // No thanks (-1 points)
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="w-12 h-12 mx-auto text-orange-400 animate-pulse mb-4" />
        <p className="text-gray-300">Loading overall results...</p>
      </div>
    );
  }

  const aggregatedResults = getAggregatedResults();
  console.log('🔍 OverallResults aggregatedResults:', aggregatedResults);
  console.log('🔍 OverallResults aggregatedResults length:', aggregatedResults.length);
  
  if (aggregatedResults.length === 0) {
    console.log('❌ OverallResults: No aggregated results, showing "No Votes Yet" message');
    return (
      <div className="text-center py-12">
        <BarChart3 className="w-16 h-16 mx-auto text-gray-500 mb-4" />
        <h3 className="text-xl font-semibold text-gray-400 mb-2">No Votes Yet</h3>
        <p className="text-gray-500">
          No votes have been cast across any categories yet.
        </p>
      </div>
    );
  }


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900 to-orange-900 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-orange-300 font-bold text-lg flex items-center gap-2">
              <TrendingUp size={20} />
              Overall Survey Results
            </h3>
            <p className="text-orange-200 text-sm">
              Movies ranked by average score across all categories
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-white">{aggregatedResults.length}</div>
            <div className="text-orange-200 text-sm">Movies Rated</div>
          </div>
        </div>
      </div>

      {/* Simple Ranked List */}
      <div className="space-y-3">
        {aggregatedResults.map((result, index) => (
          <div 
            key={result.movieId} 
            className="bg-gray-700 border border-orange-500 rounded-lg p-4 cursor-pointer hover:bg-gray-600 hover:border-orange-400 transition-all duration-200 transform hover:scale-[1.02]"
            onClick={() => onMovieClick && onMovieClick(result.movie)}
            title="Click to view movie details"
          >
            <div className="flex items-center gap-4">
              {/* Rank */}
              <div className="text-2xl font-bold text-orange-400 w-12 text-center">
                #{index + 1}
              </div>
              
              {/* Movie Poster */}
              <div className="flex-shrink-0">
                <MoviePoster
                  movie={result.movie}
                  size="sm"
                  className="w-16 h-24 object-cover rounded border-2 border-orange-600"
                />
              </div>
              
              {/* Movie Info */}
              <div className="flex-1">
                <h4 className="font-bold text-white text-lg group-hover:text-orange-200">{result.movie.title}</h4>
                <p className="text-gray-300 text-sm mb-2">{result.movie.year}</p>
                <div className="flex items-center gap-4 text-sm text-gray-400">
                  <span>{result.totalVotes} unique votes</span>
                </div>
              </div>
              
              {/* Score */}
              <div className="text-right">
                <div className={`text-3xl font-bold ${getScoreColor(result.averageScore)} mb-1`}>
                  {getScoreEmoji(result.averageScore)} {result.averageScore.toFixed(1)}
                </div>
                <div className="text-xs text-gray-400">
                  Average Score
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OverallResults;
